import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { trackGa4Event } from '@/lib/ga4';
import { getGa4AttributionFields, clearSignupAttribution } from '@/lib/signupAttribution';
import PasswordSetupFields from '@/components/PasswordSetupFields';
import OnboardingLoadingScreen from '@/components/OnboardingLoadingScreen';
import { passwordsMatch as computePasswordsMatch, allPasswordRequirementsMet } from '@/lib/passwordRequirements';

const EVA_LOGO_URL = '/vistry-icon.png';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'set_password', 'success', 'error', 'expired', 'already_verified', 'pending_approval'
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token] = useState(() => searchParams.get('token') || window.history.state?.evaToken || '');

  // EIP-433: the token is a single-use secret that arrived in the URL, so it
  // must not stay in the address bar, the history entry, or any Referer this
  // page sends. Captured into state first so everything below keeps working,
  // then also parked in history.state so a refresh mid-form still has it
  // instead of landing on the invalid-link card. Raw replaceState rather than
  // the router's navigate: this rewrites the current entry only and leaves
  // react-router's own state (idx, key) intact by spreading it.
  useEffect(() => {
    if (!searchParams.get('token') || !token) return;
    window.history.replaceState(
      { ...window.history.state, evaToken: token },
      document.title,
      window.location.pathname
    );
  }, [searchParams, token]);
  
  const passwordsMatch = computePasswordsMatch(password, confirmPassword);
  const allRequirementsMet = allPasswordRequirementsMet(password);
  
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('error');
        setError('No verification token provided');
        return;
      }
      
      try {
        const response = await api.post('/auth/verify-email', { token });
        setEmail(response.data.email || '');
        setFirstName(response.data.first_name || '');
        setStatus('set_password');
      } catch (err) {
        const errorMessage = err.response?.data?.detail || 'Verification failed';
        
        if (errorMessage.toLowerCase().includes('already been verified')) {
          setStatus('already_verified');
        } else if (errorMessage.toLowerCase().includes('expired')) {
          setStatus('expired');
        } else {
          setStatus('error');
          setError(errorMessage);
        }
      }
    };
    
    verifyToken();
  }, [token]);
  
  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast.error('Password does not meet requirements');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/complete-signup', { token, password });
      
      // Check for 202 Accepted - pending approval (axios treats it as success)
      if (response.status === 202 && response.data?.status === 'pending_approval') {
        setTenantName(response.data.tenant_name || '');
        setStatus('pending_approval');
        trackGa4Event('tenant_request_created', {
          ...getGa4AttributionFields(),
          request_status: 'pending',
        });
        clearSignupAttribution();
        return;
      }

      // Store tokens and user data
      localStorage.setItem('eva_token', response.data.access_token);
      localStorage.setItem('eva_user', JSON.stringify(response.data.user));
      if (response.data.tenant) {
        localStorage.setItem('eva_tenant', JSON.stringify(response.data.tenant));
      }

      setTenantName(response.data.tenant?.display_name || '');
      setStatus('success');
      toast.success('Account activated!');
      trackGa4Event('signup_completed', {
        ...getGa4AttributionFields(),
        tenant_status: response.data.tenant?.subscription_status || '',
      });
      clearSignupAttribution();
    } catch (err) {
      // Check for 202 Accepted - pending approval (in case it's thrown as error)
      if (err.response?.status === 202 && err.response?.data?.status === 'pending_approval') {
        setTenantName(err.response.data.tenant_name || '');
        setStatus('pending_approval');
        return;
      }
      
      const errorMessage = err.response?.data?.detail || 'Failed to complete signup';
      toast.error('Error', { description: typeof errorMessage === 'string' ? errorMessage : errorMessage.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };
  
  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12 text-center">
            <img src={EVA_LOGO_URL} alt="Eva" className="w-16 h-16 rounded-xl mx-auto mb-5" />
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-5" />
            <h2 className="text-xl font-semibold text-foreground">Verifying your email...</h2>
            <p className="text-muted-foreground mt-2.5">Please wait a moment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Set password state
  if (status === 'set_password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-10 pb-10">
            <div className="text-center mb-8">
              <img src={EVA_LOGO_URL} alt="Eva" className="w-12 h-12 rounded-xl mx-auto mb-5" />
              <h2 className="text-2xl font-bold text-foreground">Set your password</h2>
              <p className="text-muted-foreground mt-1.5">
                Welcome{firstName ? `, ${firstName}` : ''}! Create a password for <strong>{email}</strong>
              </p>
            </div>
            
            <form onSubmit={handleCompleteSignup} className="space-y-5" autoComplete="on" name="complete-signup">
              {/* Visually hidden but present so the browser's password
                  manager has a username field to associate the saved
                  credential with -- otherwise its save prompt shows a blank
                  "Username" instead of the actual email. */}
              <input
                type="email"
                name="username"
                autoComplete="username"
                value={email}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
              />
              {/* Password */}
              <PasswordSetupFields
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                showConfirmPassword={showConfirmPassword}
                setShowConfirmPassword={setShowConfirmPassword}
              />

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading || !allRequirementsMet || !passwordsMatch}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Activating account...
                  </>
                ) : (
                  'Complete Signup'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success state -- show the personality loading screen while the backend
  // best-effort seeds a starter KB doc (see kb_seed_service.py), then redirect.
  if (status === 'success') {
    return (
      <OnboardingLoadingScreen
        companyName={tenantName}
        onDone={() => {
          navigate('/');
          window.location.reload();
        }}
      />
    );
  }
  
  // Pending approval state
  if (status === 'pending_approval') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-warning-text" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Email verified!</h2>
            <p className="text-muted-foreground mb-4">
              Your email has been successfully verified.
            </p>
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 mb-6 text-left">
              <h4 className="font-medium text-warning-text text-sm mb-2">Pending admin approval</h4>
              <p className="text-sm text-warning-text">
                {tenantName ? (
                  <>Your request to join <strong>{tenantName}</strong> is being reviewed. </>
                ) : (
                  <>Your account is being reviewed by an administrator. </>
                )}
                You'll receive an email once your access has been approved.
              </p>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              This usually takes 1-2 business days. If you have any questions, please contact your organization's administrator.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
              Back to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Already verified state
  if (status === 'already_verified') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Already verified</h2>
            <p className="text-muted-foreground mb-8">
              This email has already been verified. You can log in to your account.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Go to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Expired state
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-warning-text" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Link expired</h2>
            <p className="text-muted-foreground mb-8">
              This verification link has expired. Please sign up again to receive a new link.
            </p>
            <div className="space-y-4">
              <Button onClick={() => navigate('/signup')} className="w-full">
                Sign up again
              </Button>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardContent className="pt-12 pb-12">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">Verification failed</h2>
          <p className="text-muted-foreground mb-8">
            {error || 'We could not verify your email. The link may be invalid or expired.'}
          </p>
          <div className="space-y-4">
            <Button onClick={() => navigate('/signup')} className="w-full">
              Sign up again
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Back to login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
